/**
 * ExtranetLogin — Fase 4 Extranet KB
 * Página de login por tenant: /extranet/:tenantSlug/login
 */
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ExtranetAuthProvider, useExtranetAuth } from '@/hooks/useExtranetAuth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Loader2, BookOpen } from 'lucide-react';

import { supabase } from '@/integrations/supabase/client';
import { resolveAuthError, type AuthErrorInfo } from '@/lib/extranet/authErrors';
import { ExtranetAuthAlert } from '@/components/kb/extranet/ExtranetAuthAlert';

function LoginInner() {
  const { tenantSlug = '' } = useParams<{ tenantSlug: string }>();
  const navigate = useNavigate();
  const { signIn, user, externalUser, ready } = useExtranetAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [branding, setBranding] = useState<{ name: string; logo_url: string | null } | null>(null);
  const [signupEnabled, setSignupEnabled] = useState(false);
  const [alertInfo, setAlertInfo] = useState<AuthErrorInfo | null>(null);

  // Carregar branding do tenant (público)
  useEffect(() => {
    if (!tenantSlug) return;
    (async () => {
      // Igualdade exata (evita metacaracteres do ilike como % ou _)
      const { data } = await supabase
        .from('tenants')
        .select('name, logo_url, extranet_signup_enabled')
        .eq('slug', tenantSlug.trim().toLowerCase())
        .maybeSingle();
      if (data) {
        setBranding({ name: data.name, logo_url: data.logo_url });
        setSignupEnabled(!!data.extranet_signup_enabled);
      }
    })();
  }, [tenantSlug]);

  // Se já autenticado, redireciona
  useEffect(() => {
    if (ready && user && externalUser) {
      if (externalUser.is_active) navigate(`/extranet/${tenantSlug}`, { replace: true });
      else navigate(`/extranet/${tenantSlug}/aguardando`, { replace: true });
    }
  }, [ready, user, externalUser, tenantSlug, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setAlertInfo(null);
    setSubmitting(true);
    const result = await signIn(email.trim(), password);
    setSubmitting(false);

    // Fluxo especial: aguardando aprovação — redireciona com aviso
    if (result.can_access === false && result.redirect_to) {
      navigate(result.redirect_to, { replace: true });
      return;
    }

    if (!result.success) {
      setAlertInfo(
        resolveAuthError({
          backendCode: result.backendCode,
          code: result.code,
          status: result.status,
          message: result.error,
        }),
      );
      return;
    }

    if (result.redirect_to) {
      navigate(result.redirect_to, { replace: true });
    } else {
      navigate(`/extranet/${tenantSlug}`, { replace: true });
    }
  };

  return (
    <div className="min-h-screen bg-muted/30 flex items-center justify-center p-4">
      <Helmet>
        <title>Entrar — {branding?.name ?? 'Portal do Cliente'}</title>
        <meta name="robots" content="noindex" />
      </Helmet>
      <Card className="w-full max-w-md p-6 space-y-5">
        <div className="flex flex-col items-center gap-2 text-center">
          {branding?.logo_url ? (
            <img src={branding.logo_url} alt={branding.name} className="h-12 w-12 rounded object-contain" />
          ) : (
            <div className="h-12 w-12 rounded bg-primary/10 flex items-center justify-center">
              <BookOpen className="h-6 w-6 text-primary" />
            </div>
          )}
          <h1 className="text-xl font-semibold">{branding?.name ?? 'Portal do Cliente'}</h1>
          <p className="text-sm text-muted-foreground">Acesse a Base de Conhecimento</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3">
          <ExtranetAuthAlert
            info={alertInfo}
            hrefPrefix={`/extranet/${tenantSlug}`}
          />
          <div className="space-y-1.5">
            <Label htmlFor="email">E-mail</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (alertInfo) setAlertInfo(null);
              }}
              required
              autoComplete="email"
              maxLength={255}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="password">Senha</Label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                if (alertInfo) setAlertInfo(null);
              }}
              required
              autoComplete="current-password"
              minLength={8}
              maxLength={200}
            />
          </div>
          <Button type="submit" className="w-full" disabled={submitting}>
            {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Entrar
          </Button>
        </form>

        <div className="flex items-center justify-between text-sm">
          <Link
            to={`/extranet/${tenantSlug}/esqueci-senha`}
            className="text-muted-foreground hover:text-foreground"
          >
            Esqueci a senha
          </Link>
          {signupEnabled && (
            <Link
              to={`/extranet/${tenantSlug}/cadastro`}
              className="text-primary hover:underline"
            >
              Solicitar cadastro
            </Link>
          )}
        </div>
      </Card>
    </div>
  );
}

export default function ExtranetLogin() {
  const { tenantSlug = '' } = useParams<{ tenantSlug: string }>();
  return (
    <ExtranetAuthProvider tenantSlug={tenantSlug}>
      <LoginInner />
    </ExtranetAuthProvider>
  );
}
